import { HttpService } from 'http/httpService'
import { LoggingService, EventType, ActionEventData } from 'http/loggingService'

interface ProductDetails {
    internalId: string
    uuid: string
    name: string
}

export interface Translation {
    text: string
    language: string
}

export interface VenueDetails {
    locationId: string
    locationName: string
    googlePlaceId: string
    products: ProductDetails[]
}

export interface RawProductData {
    accountSlug: string
    internalId: string
    uuid: string
    title: Translation[]
    description: Translation[]
    /** Present when requested with `include=lowest_day_price`; "0.00" when nothing is on sale today. */
    lowestDayPrice?: string
    /** Present when requested with `include=merchant`. */
    merchant?: {
        name: string
        phoneNumber: string
        currency: string
    }
    location: {
        id: string
        name: string
        googlePlaceId: string
        address: string
        latitude: string
        longitude: string
    }
}

export interface ListingOption {
    articleUUID: string
    category: string[]
    /** The ticket Google shows as the venue's standard admission; at most one per listing. */
    baseTicket?: boolean
    /** Google-only ticket text per language; a language left empty falls back to the checkout text. */
    description?: Translation[]
    landingPage?: string | null
    refundTimeLimitSec?: number | null
    refundPercentage?: number | null
}

/** Why backoffice refuses to put a ticket in the feed, as returned by the things_to_do switch. */
export type FeedRefusal =
    | 'article_archived'
    | 'article_disabled'
    | 'unsupported_business_type'
    | 'article_expired'
    | 'no_price'

export class FeedRefusedError extends Error {
    constructor(public readonly code: FeedRefusal | string) {
        super(`Ticket refused for the Google feed: ${code}`)
    }
}

/** A sellable article the picker can add, from the backoffice filtered list. */
export interface TicketCandidate {
    id: number
    uuid: string
    name: string
    internalName: string | null
    businessType: string | null
    enabled: boolean
    archived: boolean
    thingsToDoFeed: boolean
}

export interface GoogleListingItem {
    id: string
    slug: string
    locationId: string
    googlePlaceId: string
    includeInFeed: boolean
    title: string
    image: string
    description: Translation[]
    options: ListingOption[]
}

export class GoogleTTDService {
    constructor(
        private httpService: HttpService,
        private loggingService: LoggingService,
        private backofficeEndpoint: string,
        private aionEndpoint: string,
    ) {}

    getProducts = async (accountSlug: string): Promise<RawProductData[]> => {
        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/tickets/articles?account=${accountSlug}&enabled=true&archived=false&things_to_do=true&include=location,lowest_day_price,merchant`,
            {
                method: 'GET',
            },
        )
        if (!response.ok) {
            throw new Error('Unable to get account details')
        }
        return await response.json()
    }

    /** Single visit tickets and season passes of the account, flagged or not, for the ticket picker. */
    getTicketCandidates = async (accountSlug: string): Promise<TicketCandidate[]> => {
        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/articles/filtered/?business_type=single_visit_ticket,season_pass`,
            { method: 'GET' },
        )
        if (!response.ok) {
            throw new Error('Unable to get the ticket list')
        }
        return await response.json()
    }

    /**
     * Puts one ticket in or out of the Google feed on the backoffice side.
     * Enabling is refused with a FeedRefusedError carrying the backoffice reason code.
     */
    setTicketInFeed = async (accountSlug: string, articleUuid: string, enabled: boolean): Promise<void> => {
        const logEventType: EventType = 'google_ttd_item_updated'
        const logEventData: ActionEventData = {
            category: 'google_ttd',
            payload: { articleUuid, enabled },
        }
        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/articles/${articleUuid}/things_to_do/`,
            {
                method: 'PATCH',
                body: JSON.stringify({ enabled }),
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            },
        )
        if (response.status === 400) {
            const body = await response.json().catch(() => ({}))
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new FeedRefusedError(body?.error?.code ?? body?.code ?? 'refused')
        }
        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to update the ticket feed flag')
        }
        this.loggingService.logAction(logEventType, logEventData)
    }

    getGoogleTTDItems = async (accountSlug: string): Promise<GoogleListingItem[]> => {
        const response = await this.httpService.fetch(`${this.aionEndpoint}ttd/products?slug=${accountSlug}`, {
            method: 'GET',
        })
        if (!response.ok) {
            throw new Error('Unable to get Google TTD items')
        }
        return await response.json()
    }

    createGoogleTTDItem = async (accountSlug: string, payload: GoogleListingItem): Promise<GoogleListingItem> => {
        const logEventType: EventType = 'google_ttd_item_created'
        const logEventData: ActionEventData = {
            category: 'google_ttd',
            payload: { payload },
        }

        let response = await this.httpService.fetch(`${this.aionEndpoint}ttd/product?slug=${accountSlug}`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        })

        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to create a Google TTD item')
        }
        this.loggingService.logAction(logEventType, logEventData)
        return await response.json()
    }

    updateGoogleTTDItem = async (accountSlug: string, id: string, payload: GoogleListingItem): Promise<void> => {
        const logEventType: EventType = 'google_ttd_item_updated'
        const logEventData: ActionEventData = {
            category: 'google_ttd',
            payload: { googleTTDItemId: id, payload },
        }

        let response = await this.httpService.fetch(
            `${this.aionEndpoint}ttd/product?slug=${accountSlug}&id=${encodeURIComponent(id)}`,
            {
                method: 'PUT',
                body: JSON.stringify(payload),
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            },
        )

        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to update the Google TTD item')
        }
        this.loggingService.logAction(logEventType, logEventData)
        return await response.json()
    }

    async deleteGoogleTTDItem(accountSlug: string, id: string): Promise<void> {
        const logEventType: EventType = 'google_ttd_item_deleted'
        const logEventData: ActionEventData = {
            category: 'google_ttd',
            payload: { googleTTDItemId: id },
        }

        let response = await this.httpService.fetch(
            `${this.aionEndpoint}ttd/product?slug=${accountSlug}&id=${encodeURIComponent(id)}`,
            {
                method: 'DELETE',
            },
        )

        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to delete Google TTD item')
        }
        this.loggingService.logAction(logEventType, logEventData)
    }
}
